import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import {
  X, Activity, CheckCircle2, XCircle, Clock,
  CreditCard, Globe, Server, Database, Phone,
  User, Search, AlertTriangle, ArrowRight, Zap, RefreshCw, Hash
} from "lucide-react";

export default function TransactionAnalyzerModal({ txnId, role = "admin", onClose }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!txnId) return;
    setLoading(true);
    setError("");
    api.get(`/${role}/recharges/${txnId}/analysis`)
      .then(res => setData(res.data))
      .catch(err => setError(err.response?.data?.detail || "Transaction not found or access denied."))
      .finally(() => setLoading(false));
  }, [txnId, role]);

  const fmtDate = (d) => d ? d.replace("T", " ").substring(0, 19) : "—";

  if (loading) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(15,23,42,0.6)", backdropFilter: "blur(6px)" }}>
        <div className="bg-white rounded-2xl p-8 flex flex-col items-center shadow-2xl">
          <RefreshCw className="w-8 h-8 text-brand-primary animate-spin mb-4" />
          <p className="text-slate-600 font-semibold animate-pulse">Analyzing Transaction Pipeline...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(15,23,42,0.7)", backdropFilter: "blur(6px)" }}>
      <div className="bg-slate-50 w-full max-w-4xl h-[85vh] rounded-2xl shadow-2xl flex flex-col overflow-hidden relative animate-in fade-in zoom-in-95 duration-200">
        
        {/* Header */}
        <div className="bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between shrink-0 shadow-sm z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-brand-primary/10 flex items-center justify-center text-brand-primary">
              <Activity className="w-5 h-5" />
            </div>
            <div>
              <h2 className="font-heading font-bold text-lg text-slate-800 flex items-center gap-2">
                Pipeline Analysis
                {data && <span className="text-xs bg-slate-100 text-slate-500 px-2 py-0.5 rounded font-mono border border-slate-200">{data.recharge.txn_no}</span>}
              </h2>
              <p className="text-xs text-slate-500">End-to-end diagnostic view for transaction</p>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {error ? (
            <div className="bg-red-50 border border-red-100 rounded-xl p-8 text-center max-w-md mx-auto mt-10">
              <AlertTriangle className="w-12 h-12 text-red-400 mx-auto mb-4" />
              <h3 className="text-lg font-bold text-red-700 mb-2">Analysis Failed</h3>
              <p className="text-sm text-red-600/80">{error}</p>
              <button onClick={onClose} className="mt-6 px-4 py-2 bg-red-100 text-red-700 rounded-lg text-sm font-semibold hover:bg-red-200 transition">Close Analyzer</button>
            </div>
          ) : data && (
            <div className="space-y-6 max-w-3xl mx-auto">
              
              {/* Context Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
                  <p className="text-[10px] uppercase font-bold text-slate-400 tracking-wider mb-1">Status</p>
                  <p className={`font-bold text-sm ${data.recharge.status === 'success' ? 'text-green-600' : data.recharge.status === 'processing' ? 'text-blue-600' : 'text-red-600'}`}>
                    {data.recharge.status.toUpperCase()}
                  </p>
                </div>
                <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
                  <p className="text-[10px] uppercase font-bold text-slate-400 tracking-wider mb-1">Amount</p>
                  <p className="font-bold text-sm text-slate-800">₹{Number(data.recharge.amount).toFixed(2)}</p>
                </div>
                <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
                  <p className="text-[10px] uppercase font-bold text-slate-400 tracking-wider mb-1">Gateway</p>
                  <p className="font-bold text-sm text-slate-800 capitalize">{data.recharge.gateway}</p>
                </div>
                <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
                  <p className="text-[10px] uppercase font-bold text-slate-400 tracking-wider mb-1">Date</p>
                  <p className="font-bold text-xs text-slate-800 font-mono mt-0.5">{fmtDate(data.recharge.created_at)}</p>
                </div>
              </div>

              {/* Entities Context */}
              <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm flex flex-col md:flex-row gap-4 divide-y md:divide-y-0 md:divide-x divide-slate-100">
                <div className="flex-1 flex items-start gap-3">
                  <User className="w-5 h-5 text-slate-400 mt-0.5" />
                  <div>
                    <p className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">Customer</p>
                    <p className="text-sm font-semibold text-slate-800">{data.customer?.name || "N/A"}</p>
                    <p className="text-xs text-slate-500 font-mono">{data.customer?.mobile || ""}</p>
                  </div>
                </div>
                {data.dealer && (
                  <div className="flex-1 flex items-start gap-3 pt-4 md:pt-0 md:pl-4">
                    <Zap className="w-5 h-5 text-purple-400 mt-0.5" />
                    <div>
                      <p className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">Initiated By (Dealer)</p>
                      <p className="text-sm font-semibold text-slate-800">{data.dealer.name}</p>
                      <p className="text-xs text-slate-500 font-mono">{data.dealer.mobile}</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Timeline */}
              <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-1">
                <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2">
                  <Server className="w-4 h-4 text-brand-primary" />
                  <h3 className="font-bold text-slate-700 text-sm">Execution Timeline</h3>
                </div>
                <div className="p-5">
                  <div className="relative border-l-2 border-slate-200 ml-3 space-y-8 pb-4">
                    
                    {/* 1. Order Created */}
                    <div className="relative pl-6">
                      <div className="absolute w-4 h-4 bg-blue-500 rounded-full -left-[9px] top-1 ring-4 ring-white" />
                      <p className="text-xs text-slate-400 font-mono mb-1">{fmtDate(data.recharge.created_at)}</p>
                      <h4 className="text-sm font-bold text-slate-800">Order Initiated</h4>
                      <div className="mt-2 bg-slate-50 border border-slate-200 rounded p-3 text-xs font-mono text-slate-600">
                        ID: {data.recharge.id}
                        <br/>Product: {data.recharge.product_id}
                        {data.recharge.gateway_order_id && <><br/>Razorpay Order: {data.recharge.gateway_order_id}</>}
                      </div>
                    </div>

                    {/* 2. Webhooks */}
                    {data.webhooks?.map(wh => (
                      <div key={wh.id} className="relative pl-6">
                        <div className={`absolute w-4 h-4 rounded-full -left-[9px] top-1 ring-4 ring-white ${wh.event_type.includes('captured') ? 'bg-green-500' : 'bg-amber-500'}`} />
                        <p className="text-xs text-slate-400 font-mono mb-1">{fmtDate(wh.created_at)}</p>
                        <h4 className="text-sm font-bold text-slate-800 flex items-center gap-2">
                          <Globe className="w-3.5 h-3.5 text-slate-400" /> Webhook: {wh.event_type}
                        </h4>
                        <div className="mt-2 bg-slate-50 border border-slate-200 rounded p-3 text-xs overflow-x-auto">
                          <pre className="text-slate-600 font-mono">{JSON.stringify(wh.payload_summary, null, 2)}</pre>
                        </div>
                      </div>
                    ))}

                    {/* 3. Wallet */}
                    {data.wallet_transactions?.map(wt => (
                      <div key={wt.id} className="relative pl-6">
                        <div className={`absolute w-4 h-4 rounded-full -left-[9px] top-1 ring-4 ring-white ${wt.type === 'deduction' ? 'bg-orange-500' : 'bg-teal-500'}`} />
                        <p className="text-xs text-slate-400 font-mono mb-1">{fmtDate(wt.timestamp)}</p>
                        <h4 className="text-sm font-bold text-slate-800 flex items-center gap-2">
                          <CreditCard className="w-3.5 h-3.5 text-slate-400" /> 
                          Wallet {wt.type === 'deduction' ? 'Deducted' : 'Refunded'}
                        </h4>
                        <p className="text-xs text-slate-500 mt-1">Amount: ₹{Number(wt.amount).toFixed(2)} | Balance After: ₹{Number(wt.balance_after).toFixed(2)}</p>
                      </div>
                    ))}

                    {/* 4. Dish API Logs */}
                    {data.api_logs?.map(log => (
                      <div key={log.id} className="relative pl-6">
                        <div className={`absolute w-4 h-4 rounded-full -left-[9px] top-1 ring-4 ring-white ${log.status === 'success' ? 'bg-green-500' : log.status === 'error' ? 'bg-red-500' : 'bg-slate-400'}`} />
                        <p className="text-xs text-slate-400 font-mono mb-1">{fmtDate(log.timestamp)}</p>
                        <h4 className="text-sm font-bold text-slate-800 flex items-center gap-2">
                          <Database className="w-3.5 h-3.5 text-slate-400" /> DishTV API: {log.event_label}
                        </h4>
                        <p className="text-xs text-slate-500 mb-2">{log.message}</p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                          {log.request_data && (
                            <div className="bg-slate-50 border border-slate-200 rounded p-2 text-[10px] overflow-x-auto">
                              <p className="font-bold text-slate-400 mb-1 uppercase">Request</p>
                              <pre className="text-slate-600">{JSON.stringify(log.request_data, null, 2)}</pre>
                            </div>
                          )}
                          {log.response_data && (
                            <div className="bg-slate-50 border border-slate-200 rounded p-2 text-[10px] overflow-x-auto">
                              <p className="font-bold text-slate-400 mb-1 uppercase">Response</p>
                              <pre className="text-slate-600">{JSON.stringify(log.response_data, null, 2)}</pre>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}

                    {/* 5. Final State */}
                    <div className="relative pl-6">
                      <div className={`absolute w-4 h-4 rounded-full -left-[9px] top-1 ring-4 ring-white ${data.recharge.status === 'success' ? 'bg-green-500' : data.recharge.status === 'processing' ? 'bg-blue-500' : 'bg-red-500'}`} />
                      <p className="text-xs text-slate-400 font-mono mb-1">{fmtDate(data.recharge.updated_at)}</p>
                      <h4 className="text-sm font-bold text-slate-800">Final Terminal State</h4>
                      <p className="text-xs text-slate-500 mt-1 uppercase font-bold">{data.recharge.status}</p>
                    </div>

                  </div>
                </div>
              </div>

            </div>
          )}
        </div>
      </div>
    </div>
  );
}
